// @greenlight-spec: 11-observability.md#zombie-app-detection
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAppActivityActions } from './appActivityActions';
import { PauseAppDialog } from './PauseAppDialog';
import type { AppInactivityNotice } from '@greenlight/shared';

export interface InactivityBannerProps {
  appId: string;
  appName: string;
  inactivity: AppInactivityNotice | undefined;
  /** The app's public address, when it has one — the "keep it running" target. */
  deployUrl: string | null;
}

const days = (n: number): string => `${n} day${n === 1 ? '' : 's'}`;

/**
 * The warning the inactivity email points at, on the page it links to.
 *
 * It renders only while there is a decision to make — an app the scan has warned
 * about — and says the same thing the mail said, so an owner arriving from their
 * inbox recognises the page. An already-paused app is not shown a countdown: the
 * status badge beside it already reads Paused, and the power menu offers Restore.
 *
 * Two answers, because an owner has two. Keeping the app is **Open the app**,
 * which is not a Greenlight action at all: using the app is itself the activity
 * signal that resets the clock (docs/11 §Activity signals). An app with no
 * address cannot be opened, so it falls back to **Still needed**, which writes
 * the same reset directly. **Pause it now** is for the owner who realises the
 * app is finished with: it is the confirmed kill-switch the power menu offers,
 * so agreeing with the warning is not slower than ignoring it for a month.
 */
export function InactivityBanner({
  appId,
  appName,
  inactivity,
  deployUrl,
}: InactivityBannerProps): React.JSX.Element | null {
  const activity = useAppActivityActions({ appId, appName });
  const [pauseOpen, setPauseOpen] = React.useState(false);

  if (!inactivity || inactivity.activity_state !== 'inactive') return null;

  const idle =
    inactivity.days_since_activity === null
      ? 'Nobody has used this app recently'
      : `Nobody has used this app for ${days(inactivity.days_since_activity)}`;
  const when =
    inactivity.days_until_pause === null
      ? 'It is scheduled to be paused.'
      : `It is scheduled to be paused in ${days(inactivity.days_until_pause)}.`;

  return (
    <Alert variant="warning" className="mt-4" data-testid="app-inactivity-banner">
      <AlertTitle>{appName} is scheduled to be paused</AlertTitle>
      <AlertDescription>
        <p>
          {idle}. {when} Pausing is reversible and nothing is deleted — opening the app is enough to
          keep it running.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {deployUrl ? (
            <Button asChild size="sm">
              <a href={deployUrl} target="_blank" rel="noopener noreferrer">
                Open the app
              </a>
            </Button>
          ) : (
            <Button
              size="sm"
              disabled={activity.pending !== null}
              onClick={() => void activity.extend()}
            >
              {activity.pending === 'extend' ? 'Confirming…' : 'Still needed'}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            disabled={activity.pending !== null}
            onClick={() => setPauseOpen(true)}
          >
            Pause it now
          </Button>
        </div>
      </AlertDescription>
      <PauseAppDialog
        appId={appId}
        appName={appName}
        open={pauseOpen}
        onOpenChange={setPauseOpen}
        busy={activity.pending !== null}
      />
    </Alert>
  );
}
